import {
    Shield, KeyRound, Lock, Fingerprint, Database, ShieldCheck, Eye,
    Server, Globe, RefreshCw, FileLock2, Check,
} from "lucide-react";

/**
 * Security · Compliance · Audit dark section for the Toothmatrix landing.
 * Self-contained — no parent state, no fetch, no router dependency.
 * Pure copy + the SECURITY_FEATURES enumeration below.
 */
export default function SecuritySection() {
    return (
        <section data-testid="toothmatrix-security" className="relative bg-[#0B1120] text-white py-24 lg:py-32 overflow-hidden">
            {/* subtle grid backdrop */}
            <div aria-hidden="true" className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
            }} />
            <div aria-hidden="true" className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-cyan-500/20 blur-3xl" />
            <div aria-hidden="true" className="absolute -bottom-32 -left-32 w-[460px] h-[460px] rounded-full bg-emerald-500/15 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-6">
                <header className="max-w-3xl mb-14">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-mono tracking-[0.18em] uppercase text-cyan-300 mb-5">
                        <Shield size={12} /> Security · Compliance · Audit
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Patient data is treated like the medical record it is.
                    </h2>
                    <p className="mt-5 text-[15px] text-slate-300 leading-relaxed max-w-2xl">
                        Toothmatrix is built from day one for clinical-grade data handling. Every layer — from password storage to network transport to per-tenant database isolation — uses what hospitals and banks actually deploy in production, not what looks good on a slide.
                    </p>
                </header>

                {/* TRUST STAT STRIP */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="security-stats">
                    <TrustStat value="AES-256" label="Encryption at rest" />
                    <TrustStat value="TLS 1.3" label="Encryption in transit" />
                    <TrustStat value="5-strike" label="Brute-force lockout · 15 min" />
                    <TrustStat value="bcrypt" label="Password hashing · industry standard" />
                </div>

                {/* FEATURE GRID — what's under the hood */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {SECURITY_FEATURES.map((f) => (
                        <SecurityCard key={f.title} {...f} />
                    ))}
                </div>

                {/* COMPLIANCE BANNER */}
                <div className="mt-14 rounded-3xl bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-amber-500/10 border border-white/10 p-7 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-6" data-testid="security-compliance-banner">
                    <div className="h-14 w-14 rounded-2xl bg-white/10 border border-white/20 text-emerald-300 flex items-center justify-center shrink-0">
                        <FileLock2 size={26} />
                    </div>
                    <div className="flex-1">
                        <div className="text-[11px] font-mono tracking-[0.18em] uppercase text-emerald-300 mb-1">· Qatar PDPPL — Law No. 13 of 2016</div>
                        <h3 className="font-display text-2xl tracking-tight">Built for Qatar&apos;s Personal Data Privacy Protection Law.</h3>
                        <p className="mt-2 text-[13.5px] text-slate-300 leading-relaxed max-w-3xl">
                            Mandatory consent gates on every signup and case submission. Patient consent inventory accessible to every tenant admin. A live &quot;Bill of Rights&quot; the data subject (your patient) can read in their language. Immutable audit trail of every personal-data action — required by Article 16 of PDPPL.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            <ComplianceBadge label="Lawful basis tracking" />
                            <ComplianceBadge label="Consent withdrawal" />
                            <ComplianceBadge label="Data export · Art. 13" />
                            <ComplianceBadge label="Erasure right · Art. 14" />
                            <ComplianceBadge label="Audit log · Art. 16" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const SECURITY_FEATURES = [
    { icon: KeyRound,    title: "Brute-force lockout",                body: "5 failed login attempts within 15 minutes auto-locks the account. Every attempt is recorded with IP + timestamp. Repeat offenders trigger an instant WhatsApp alert to the lab admin.", tone: "rose" },
    { icon: Lock,        title: "Bcrypt password hashing",           body: "Passwords are never stored. We hash with bcrypt (cost factor 12) so even a stolen database is useless — cracking a single password takes years on modern GPUs.", tone: "cyan" },
    { icon: Fingerprint, title: "httpOnly auth cookies",             body: "Auth tokens live in SameSite httpOnly cookies — invisible to JavaScript, immune to XSS-based token theft. Signed JWTs with HS256 rotation on every session.", tone: "amber" },
    { icon: Database,    title: "Per-tenant data isolation",         body: "Every query is scoped by tenant_id at the database layer. One clinic can never see another clinic's cases, patients, invoices, or staff — enforced server-side, not client-side.", tone: "emerald" },
    { icon: ShieldCheck, title: "AES-256 at rest, TLS 1.3 in transit", body: "MongoDB Atlas encrypts every byte on disk with AES-256. Every API call is TLS 1.3-encrypted between the clinic's browser and our servers. No clinical data ever travels in the clear.", tone: "cyan" },
    { icon: Eye,         title: "Immutable audit log",                body: "Every login, every status change, every invoice edit, every credit movement — append-only, timestamped, attributed. The PDPPL data-subject right of audit is one query away.", tone: "slate" },
    { icon: Server,      title: "Daily encrypted backups",            body: "Automated nightly snapshots with 30-day retention. Point-in-time recovery via the Disaster Recovery admin tab. Restore individual collections without taking the live system offline.", tone: "amber" },
    { icon: Globe,       title: "Rate-limited APIs",                  body: "Sliding-window IP rate limits on signup, case verification, and password reset. Abuse triggers an instant WhatsApp alert so the merchant has off-platform breadcrumbs of attacks.", tone: "rose" },
    { icon: RefreshCw,   title: "Live PDPPL consent ledger",          body: "Every consent ever given — by patients on a case form, by tenants at signup — is logged with IP, version, and timestamp. Withdraw consent and the record is preserved forever for the audit trail.", tone: "emerald" },
];

function TrustStat({ value, label }) {
    return (
        <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 lg:p-6">
            <div className="font-display text-3xl lg:text-4xl tracking-tight text-white">{value}</div>
            <div className="text-[11px] font-mono tracking-[0.18em] uppercase text-slate-400 mt-2 leading-snug">{label}</div>
        </div>
    );
}

function SecurityCard({ icon: Icon, title, body, tone }) {
    const toneMap = {
        cyan:    { iconBg: "bg-cyan-500/10 text-cyan-300 border-cyan-400/20" },
        emerald: { iconBg: "bg-emerald-500/10 text-emerald-300 border-emerald-400/20" },
        amber:   { iconBg: "bg-amber-500/10 text-amber-300 border-amber-400/20" },
        rose:    { iconBg: "bg-rose-500/10 text-rose-300 border-rose-400/20" },
        slate:   { iconBg: "bg-white/5 text-slate-300 border-white/10" },
    };
    const t = toneMap[tone] || toneMap.slate;
    return (
        <article className="group rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 p-6 transition relative overflow-hidden" data-testid={`security-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className={`h-11 w-11 rounded-xl border ${t.iconBg} flex items-center justify-center mb-4`}>
                <Icon size={20} />
            </div>
            <h3 className="font-display text-lg tracking-tight text-white">{title}</h3>
            <p className="mt-2 text-[13px] text-slate-300/85 leading-relaxed">{body}</p>
        </article>
    );
}

function ComplianceBadge({ label }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-mono tracking-wide text-white/90">
            <Check size={11} className="text-emerald-400" /> {label}
        </span>
    );
}
